import { readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

/** H4 (decisions.md §34): Admin → Access report. */
export type FlagReason = "devices" | "cities";

export type StudentAccess = {
  userId: string;
  name: string | null;
  email: string;
  suspended: boolean;
  suspendedAt: string | null;
  devices7d: number;
  cities7d: number;
  lastSignInAt: string | null;
  flagged: boolean;
  flagReasons: FlagReason[];
};

export type AccessReport = {
  windowDays: number;
  retentionDays: number;
  thresholds: { devices: number; cities: number };
  students: StudentAccess[];
};

export type AccessHistoryEntry = {
  at: string;
  event: "sign_in" | "device_limit_reached" | "device_signed_out" | string;
  place: string | null;
  browser: string | null;
  os: string | null;
  ipAddress: string | null;
};

export type StudentAccessDetail = {
  windowDays: number;
  thresholds: { devices: number; cities: number };
  student: StudentAccess;
  history: AccessHistoryEntry[];
};

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw await readApiErrorResponse(res);
  return ((await res.json()) as { data: T }).data;
}

export async function fetchAccessReport(): Promise<AccessReport> {
  return json<AccessReport>(await apiFetch("/v1/tenant/access-report"));
}

export async function fetchStudentAccess(userId: string): Promise<StudentAccessDetail> {
  return json<StudentAccessDetail>(
    await apiFetch(`/v1/tenant/access-report/${encodeURIComponent(userId)}`),
  );
}

export async function suspendStudent(userId: string): Promise<{ signedOut: number }> {
  return json<{ signedOut: number }>(
    await apiFetch(`/v1/tenant/members/${encodeURIComponent(userId)}/suspension`, {
      method: "POST",
    }),
  );
}

export async function unsuspendStudent(userId: string): Promise<void> {
  await json<unknown>(
    await apiFetch(`/v1/tenant/members/${encodeURIComponent(userId)}/suspension`, {
      method: "DELETE",
    }),
  );
}

/** H3's device reset, reused as "Sign out everywhere". */
export async function signOutEverywhere(userId: string): Promise<{ signedOut: number }> {
  return json<{ signedOut: number }>(
    await apiFetch(`/v1/tenant/members/${encodeURIComponent(userId)}/devices`, {
      method: "DELETE",
    }),
  );
}
